import React, {useCallback, useEffect, useRef, useState} from "react";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import requestor from "requestor";

import CircularProgress from "@material-ui/core/CircularProgress";

import Permissioner from "utils/permissioner";
import {isInitialised} from "utils/uninitialised";
import setTitle from "utils/set_title";
import LlmRunDetailComponent from "../components/llm_run_detail";

import addTestAction from "modules/flowmaster/actions/workflow_test_add";
import updateTestAction from "modules/flowmaster/actions/workflow_test_update";
import deleteTestAction from "modules/flowmaster/actions/workflow_test_delete";
import rerunTestsAction from "modules/flowmaster/actions/llm_run_rerun_tests";
import getTestResultsAction from "modules/flowmaster/actions/workflow_test_results_fetch";
import rerunLlmRunAction from "modules/flowmaster/actions/llm_run_rerun";
import llmRunFetchAction from "modules/flowmaster/actions/llm_run_fetch";

import {EnhancedLlmRun, LlmRunId} from "common/flowmaster/types/llm_run";
import {ActionDefinitionWithType} from "common/flowmaster/types/task_config";
import {DataKey, Prompt, WorkflowId} from "common/flowmaster/types/workflow";
import {TestId} from "common/flowmaster/types/test";
import {User} from "common/types/user";
import {OrganisationId} from "common/types/organisation";

const REFRESH_INTERVAL_MS = 15000;

type Props = {
  llmRun: EnhancedLlmRun;
  actionDefinitions: ActionDefinitionWithType[];
  prompts: Prompt[];
  user: User;
  organisation: {id: OrganisationId};
  params: {
    organisationId: string;
    llmRunId: string;
  };
  addTest: (
    organisationId: OrganisationId,
    workflowContextId: WorkflowId,
    links: Record<string, unknown>,
    dataKey: DataKey,
    key: string,
    nameKey?: string,
  ) => void;
  updateTest: (
    organisation_id: OrganisationId,
    workflow_context_id: WorkflowId,
    testId: TestId,
    testType: string,
    value: Record<string, object>,
  ) => void;
  deleteTest: (
    organisation_id: OrganisationId,
    workflow_context_id: WorkflowId,
    testId: TestId,
  ) => void;
  rerunTests: (organisation_id: OrganisationId, llmRunId: LlmRunId) => void;
  rerunLlmRun: (
    organisation_id: OrganisationId,
    llmRunId: LlmRunId,
  ) => Promise<{value: string}>;
  getTestResults: (
    organisation_id: OrganisationId,
    llmRunId: LlmRunId,
    dataKey: DataKey,
    key: string,
  ) => void;
  fetchLlmRun: (
    organisation_id: OrganisationId,
    llmRunId: string,
  ) => Promise<void>;
};

function LlmRunDetailContainer(props: Props) {
  const shouldRenderContainer = isInitialised([
    props.llmRun,
    props.actionDefinitions,
    props.prompts,
    props.user,
  ]);

  const llmRunRef = useRef(props.llmRun);

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem("llmRunAutoRefresh");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showInternalActions, setShowInternalActions] = useState(false);
  const [showActionDetail, setShowActionDetail] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeveloperMode, setIsDeveloperMode] = useState(
    props.user.is_developer,
  );
  useEffect(() => {
    setIsDeveloperMode(props.user.is_developer);
    setShowInternalActions(props.user.is_developer);
    setShowActionDetail(props.user.is_developer);
  }, [props.user]);

  useEffect(() => {
    llmRunRef.current = props.llmRun;
  }, [props.llmRun]);

  const handleToggleShowInternalActions = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setShowInternalActions(event.target.checked);
  };
  const handleToggleShowActionDetail = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setShowActionDetail(event.target.checked);
  };
  const handleToggleDeveloperMode = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsDeveloperMode(event.target.checked);
  };

  const handleToggleAutoRefresh = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.checked;
    setAutoRefreshEnabled(newValue);
    localStorage.setItem("llmRunAutoRefresh", JSON.stringify(newValue));
  };

  const organisationId = parseInt(props.params.organisationId, 10);
  const addTestHandler = (dataKey, key) => {
    props.addTest(
      organisationId,
      props.llmRun.workflow_context_id,
      props.llmRun.links,
      dataKey,
      key,
      props.llmRun.name,
    );
  };

  const updateTestHandler = (testId, testType, value) => {
    props.updateTest(
      organisationId,
      props.llmRun.workflow_context_id,
      testId,
      testType,
      value,
    );
  };

  const deleteTestHandler = testId => {
    props.deleteTest(organisationId, props.llmRun.workflow_context_id, testId);
  };

  const rerunTestsHandler = () => {
    props.rerunTests(organisationId, props.llmRun.id);
  };

  const fetchLlmRun = () => {
    setIsRefreshing(true);
    const {llmRunId} = props.params;
    props.fetchLlmRun(organisationId, llmRunId).finally(() => {
      setIsRefreshing(false);
    });
  };

  const checkAndRefreshRun = useCallback(async () => {
    if (!autoRefreshEnabled) {
      return;
    }

    const currentRun = llmRunRef.current;
    if (!currentRun) {
      return;
    }

    const isIncomplete = !currentRun.is_complete && !currentRun.has_failed;

    if (isIncomplete) {
      setIsRefreshing(true);
      fetchLlmRun();
    }
  }, [fetchLlmRun, autoRefreshEnabled]);

  useEffect(() => {
    if (!autoRefreshEnabled) {
      return;
    }

    const refreshInterval = setInterval(
      checkAndRefreshRun,
      REFRESH_INTERVAL_MS,
    );
    return () => clearInterval(refreshInterval);
  }, [checkAndRefreshRun, autoRefreshEnabled]);

  const reruLlmRunHandler = async () => {
    props.rerunLlmRun(organisationId, props.llmRun.id).then(res => {
      window.open(
        `/organisation/${organisationId}/llm_run/${res.value}/detail`,
        "_blank",
      );
    });
  };

  const getTestResultsHandler = (dataKey: DataKey, key: string) => {
    props.getTestResults(organisationId, props.llmRun.id, dataKey, key);
  };

  if (!shouldRenderContainer || !props.user.permissions) {
    return (
      <div
        style={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  const permissioner = new Permissioner(props.user);
  if (
    !(permissioner.hasPermission("get-workflows") || permissioner.isAdmin())
  ) {
    return permissioner.getNoPermissionMessage();
  }

  setTitle(`LLM Run Detail: ${props.llmRun.data.id}`);
  return (
    <LlmRunDetailComponent
      organisationId={props.organisation.id}
      llmRun={props.llmRun}
      actionDefinitions={props.actionDefinitions}
      showInternalActions={showInternalActions}
      onInternalActionsToggle={handleToggleShowInternalActions}
      showActionDetail={showActionDetail}
      onShowActionDetailToggle={handleToggleShowActionDetail}
      isDeveloperMode={isDeveloperMode}
      onDeveloperModeToggle={handleToggleDeveloperMode}
      prompts={props.prompts}
      onAddTest={addTestHandler}
      onUpdateTest={updateTestHandler}
      onDeleteTest={deleteTestHandler}
      rerunTests={rerunTestsHandler}
      rerunLlmRun={reruLlmRunHandler}
      getTestResults={getTestResultsHandler}
      fetchLlmRun={fetchLlmRun}
      autoRefreshEnabled={autoRefreshEnabled}
      onAutoRefreshToggle={handleToggleAutoRefresh}
      isRefreshing={isRefreshing}
    />
  );
}

function select(state, props) {
  return {
    params: props.params,
    organisation: state.organisation,
    router: state.router,
    user: state.user,
    llmRun: state.llmRun,
    actionDefinitions: state.actionDefinitions,
    prompts: state.prompts,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        fetchLlmRun: llmRunFetchAction(requestor),
        addTest: addTestAction(requestor),
        updateTest: updateTestAction(requestor),
        deleteTest: deleteTestAction(requestor),
        rerunTests: rerunTestsAction(requestor),
        rerunLlmRun: rerunLlmRunAction(requestor),
        getTestResults: getTestResultsAction(requestor),
      },
      dispatch,
    ),
  };
}

export default connect(select, mapDispatchToProps)(LlmRunDetailContainer);
